import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useDriverProfile } from "@/hooks/useDriverProfile";
import { useDriverAssignments } from "@/hooks/useDriverAssignments";
import { AssignmentListCard } from "@/components/driver/AssignmentListCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { LogOut, AlertCircle, Calendar, History, ChevronDown, Menu, Truck, Container } from "lucide-react";
import auroraLogo from "@/assets/aurora-brand-logo.svg";
import { format } from "date-fns";
import type { Location } from "@/types/tms";
import { BolUploadButton } from "@/components/driver/BolUploadButton";
import { TenderUploadButton } from "@/components/driver/TenderUploadButton";
import { DriverDocViewerDialog } from "@/components/driver/DriverDocViewerDialog";
import { PlaceholderChip } from "@/components/driver/PlaceholderChip";
import { useQuery } from "@tanstack/react-query";
import { OrderWithRelations } from "@/components/schedule/types";
import { EnvironmentSwitcher, useEnvironmentBorderClass } from "@/components/layout/EnvironmentSwitcher";
import { UserProfilePopover } from "@/components/layout/UserProfilePopover";
import { DriverNavMenu } from "@/components/driver/DriverNavMenu";

function CompletedTripCard({ order, driverName }: { order: OrderWithRelations; driverName?: string }) {
  const { data: bolCount = 0 } = useQuery({
    queryKey: ["bol-images-count", order.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("order_bol_images")
        .select("id", { count: "exact", head: true })
        .eq("order_id", order.id);
      if (error) throw error;
      return count || 0;
    },
  });

  const { data: tenderCount = 0 } = useQuery({
    queryKey: ["tender-images-count", order.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("order_tender_images")
        .select("id", { count: "exact", head: true })
        .eq("order_id", order.id);
      if (error) throw error;
      return count || 0;
    },
  });

  return (
    <Card className="bg-muted/30">
      <CardContent className="py-3 px-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm">{order.unique_id}</div>
            <div className="text-xs text-muted-foreground truncate">
              {order.ship_from_city} → {order.ship_to_city}
            </div>
          </div>
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            {format(new Date(order.updated_at), "MMM d, h:mm a")}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <PlaceholderChip label="Truck ID" value={order.assigned_tractor?.name} icon={Truck} />
          <PlaceholderChip label="Trailer ID" value={order.trailer_id} icon={Container} />
        </div>

        {/* Document uploads */}
        <div className="space-y-2 pt-1 border-t">
          <BolUploadButton orderId={order.id} orderUniqueId={order.unique_id} driverName={driverName} />
          {bolCount > 0 && (
            <DriverDocViewerDialog orderId={order.id} docType="bol" imageCount={bolCount} />
          )}
          {tenderCount > 0 && (
            <DriverDocViewerDialog orderId={order.id} docType="tender" imageCount={tenderCount} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DriverDashboard() {
  const { user } = useAuth();
  const { driver, isLoading: loadingDriver } = useDriverProfile();
  const { 
    currentAssignments,
    recentlyCompleted,
    isLoading: loadingAssignments 
  } = useDriverAssignments(driver?.id);

  // Resolves the Ship From timezone for "Upcoming" cards so drivers always
  // see local pickup wall-clock time regardless of their phone's timezone.
  const { data: locations } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data } = await supabase.from("locations").select("*").eq("archived", false).order("short_code");
      return (data ?? []) as Location[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const [recentOpen, setRecentOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const envBorderClass = useEnvironmentBorderClass();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (loadingDriver) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Driver Profile Not Found
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                No driver profile is linked to your account ({user?.email}).
                Please contact your administrator to set up your driver profile.
              </p>
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header - compact for mobile */}
      <header className={`sticky top-0 z-10 flex h-14 items-center gap-3 border-b bg-card px-4 ${envBorderClass}`}>
        <Button variant="ghost" size="icon" onClick={() => setMenuOpen(true)} className="shrink-0">
          <Menu className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-bold text-foreground truncate">
              {driver.name}
            </h1>
            <EnvironmentSwitcher variant="header" />
          </div>
          {driver.manager && (
            <p className="text-xs text-muted-foreground truncate">{driver.manager}</p>
          )}
        </div>
        <div className="shrink-0">
          <UserProfilePopover compact asSheet />
        </div>
      </header>

      {/* Slide-out navigation */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 flex flex-col p-0">
          <SheetHeader className="px-4 pt-4 pb-4 border-b">
            <div className="flex items-center gap-3">
              <img src={auroraLogo} alt="Aurora logo" className="h-8 w-8 object-contain" />
              <div className="text-left">
                <SheetTitle className="text-lg font-bold">ATMS</SheetTitle>
                <p className="text-xs text-muted-foreground">Aurora Transportation Management</p>
              </div>
            </div>
          </SheetHeader>
          <DriverNavMenu onNavigate={() => setMenuOpen(false)} />
          <div className="border-t p-4">
            <EnvironmentSwitcher variant="sidebar" />
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Content - mobile padding */}
      <main className="p-4 pb-8">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Current Assignments */}
          <section>
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              My assignments
              {currentAssignments.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                  {currentAssignments.length}
                </span>
              )}
            </h2>

            {loadingAssignments ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Loading assignments...
                </CardContent>
              </Card>
            ) : currentAssignments.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center">
                  <Calendar className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">No current assignments</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {currentAssignments.map((order) => {
                  const legSeq = (order as any).leg_sequence as number | null | undefined;
                  const totalLegs = (order as any)._total_legs as number | undefined;
                  const isMultiLeg = (totalLegs ?? 1) > 1;
                  return (
                    <AssignmentListCard
                      key={order.id}
                      order={order}
                      locations={locations}
                      legSequence={legSeq ?? null}
                      isMultiLeg={isMultiLeg}
                      onClick={() => navigate(`/my-assignments/${order.id}`)}
                    />
                  );
                })}
              </div>
            )}
          </section>

          {/* Recently Completed Trips */}
          {recentlyCompleted.length > 0 && (
            <Collapsible open={recentOpen} onOpenChange={setRecentOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 w-full text-left text-base font-semibold py-2 group">
                  <History className="h-4 w-4 text-muted-foreground" />
                  Recently Completed
                  <span className="text-xs font-normal text-muted-foreground">
                    ({recentlyCompleted.length})
                  </span>
                  <ChevronDown className={`h-4 w-4 ml-auto text-muted-foreground transition-transform ${recentOpen ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-2 pt-1">
                  {recentlyCompleted.map((order) => (
                    <CompletedTripCard
                      key={order.id}
                      order={order}
                      driverName={driver.name}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </main>
    </div>
  );
}
